import { toNumber } from 'lodash';
import styled, { useTheme } from 'styled-components';
import { flexbox, space } from 'styled-system';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { StaticGoogleMap } from '@hivebrite/design-system/StaticGoogleMap';
import { FONT_BRIGHTNESS_MAPPING } from '@hivebrite/design-system/Theme';

import Map from 'hb-react/frontoffice/components/MapboxMap';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import EventDescriptionAndTabs from 'hb-react/shared/apps/Events/components/EventDetails';
import EventRelatedGroups from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventRelatedGroups';
import TicketTable from 'hb-react/shared/apps/Events/components/TicketTable';
import VirtualEventLink from 'hb-react/shared/apps/Events/components/VirtualEventLink';
import { CommentsContainer } from 'hb-react/shared/apps/Events/containers/CommentsContainer';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import { ContentRecommendation } from 'hb-react/shared/components/ContentRecommendation';
import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import IconLocation from 'hb-react/shared/icons/Location';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import { LEGACY_ENTITY_TYPES } from 'hb-react/shared/utils/entitiesContentData/constants';
import { isCommentsModuleAvailable } from 'hb-react/shared/utils/features/comments';
import ThemeProvider from 'hb-react/style/ThemeProvider';

const EventMap = () => {
  const { flagGoogleMapsComponent } = useFeatureFlag();
  const theme = useTheme();
  const { event } = useEventContext();

  const frameTheme = useFrameTheme();

  const address = event?.locations?.address ?? '';
  const lat = event?.locations?.lat ?? '0.0';
  const lng = event?.locations?.lng ?? '0.0';

  const {
    start_date: startDate,
    end_date: endDate,
    timezone: eventTimeZone,
  } = event;

  const locationIsPresent =
    event.locations &&
    event.locations.lng &&
    event.locations.lat &&
    (toNumber(event.locations.lng) !== 0 ||
      toNumber(event.locations.lat) !== 0);

  if (!locationIsPresent) {
    return null;
  }

  if (flagGoogleMapsComponent) {
    return (
      <StaticGoogleMap
        apiKey={window.__HB_ENV__.GOOGLEMAPS_API_KEY}
        errorMessage={__('StaticGoogleMap|Error loading map')}
        footer={{
          iconName: 'location-dot',
          address,
          dateRange: [
            eventTimeZone
              ? dayjs(startDate).tz(eventTimeZone).format('ll')
              : dayjs(startDate).format('ll'),
            eventTimeZone
              ? dayjs(endDate).tz(eventTimeZone).format('ll')
              : dayjs(endDate).format('ll'),
          ],
        }}
        latitude={lat}
        longitude={lng}
        zoom={13}
      />
    );
  }

  return (
    <ContentRow>
      <Box
        mt={4}
        mx={{ _: -theme.grid.colGap._, m: 0 }}
        display="block"
        css="position: relative;"
      >
        <Map
          center={{ lat, lng }}
          height={250}
          light_style
          marker={{ lat, lng }}
          enable_zoom={false}
          zoom={14}
        />
        <ThemeProvider
          theme={frameTheme.themeName}
          fontBrightness={frameTheme.fontBrightness}
        >
          <Box
            bg={frameTheme.backgroundColor}
            p={3}
            pr={5}
            alignItems="center"
            css="position: absolute; bottom: 0; left: 0; z-index: 401;"
          >
            <Box color="primary" mr={3}>
              <IconLocation height={30} width={30} />
            </Box>
            <Box display="block">
              <Box
                fontWeight={1}
                onClick={() =>
                  window.open(
                    `https://www.google.com/maps/search/${address}/${lat},${lng},11z/am=t/`,
                    `_blank`,
                  )
                }
              >
                <Dotdotdot clamp={1}>{address}</Dotdotdot>
              </Box>
            </Box>
          </Box>
        </ThemeProvider>
      </Box>
    </ContentRow>
  );
};

const EventInformations = (otherProps) => {
  const { event } = useEventContext();
  const { topic } = useTopicContext();

  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const isTicketsEvent = event.participation_type === 'tickets';
  const showComments = isCommentsModuleAvailable(
    currentUser,
    !currentNetwork?.network_configuration?.events?.comments_enabled,
  );

  const { display_related_topics: displayRelatedTopics } = event;

  const { flagUjAiContentRecommendationForumEvents } = useFeatureFlag();

  // Content recommendation hasn't been implemented for groups yet
  const isContentRecommendationVisible =
    flagUjAiContentRecommendationForumEvents &&
    currentUser &&
    !topic?.id &&
    currentNetwork?.network_configuration?.ai_content_recommendation?.enabled;

  return (
    <Box flexDirection="column" alignItems="stretch" {...otherProps}>
      {/* VirtualEventLink is conditionally rendered internally so we style it directly to avoid an
      empty ContentRow adding unwanted space to the layout */}
      <StyledVirtualEventLink event={event} />
      <ContentRow mt={0}>
        <StyledEventDescriptionAndTabs
          event={event}
          defaultTitle={__('Details')}
        />
      </ContentRow>
      {isTicketsEvent ? (
        <ContentRow>
          <TicketTable
            tickets={event.tickets}
            readOnly
            maxTicketsPerUser={event.max_tickets_per_user}
          />
        </ContentRow>
      ) : null}

      <EventMap />

      {displayRelatedTopics && (
        <EventRelatedGroups
          eventId={event.id}
          wrapper={ContentRow}
          title={() => (
            <Box font="h3-semibold" pb={3}>
              {s__('Event|Related groups')}
            </Box>
          )}
        />
      )}

      {isContentRecommendationVisible && (
        <ContentRecommendation
          contentId={event.id}
          contentType={LEGACY_ENTITY_TYPES.adminEvent}
        />
      )}

      {showComments ? (
        <ContentRow>
          <Box font="h3-semibold" pb={3}>
            {I18n.t('web.events.show.comments')}
          </Box>
          <StyledCommentsContainer eventId={event.id} likes={event.likes} />
        </ContentRow>
      ) : null}
    </Box>
  );
};

export default EventInformations;

// STYLED COMPONENTS
const ContentRow = styled(Box).attrs(({ mt }) => ({
  flexDirection: 'column',
  mt: mt ?? 6,
}))`
  line-height: 1.2;
`;

// This is not ideal but it kept changes on the existing component to a minimum
const StyledVirtualEventLink = styled(VirtualEventLink).attrs(() => ({
  mb: 6,
  mt: 2,
  flexDirection: 'column',
  alignItems: 'center',
}))`
  ${space}
  ${flexbox}

  & > div {
    justify-content: flex-start;
    width: 100%;
  }

  & .virtual-event-link-rendered-time {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  & .virtual-event-link-countdown {
    flex-wrap: nowrap;
  }
`;

// This is not ideal but it kept changes on the existing component to a minimum
const StyledEventDescriptionAndTabs = styled(EventDescriptionAndTabs)`
  padding: 0 !important;
  width: 100% !important;
  height: 100% !important;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 20px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #d6dee1;
    border-radius: 20px;
    border: 4px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8bbbf;
  }

  & > nav {
    margin-bottom: 0 !important;
  }
  & > nav > a {
    color: ${({ theme }) =>
      theme.colors[
        FONT_BRIGHTNESS_MAPPING.asphalt[theme.themeVariables.fontBrightness]
      ]};
    font-size: ${({ theme }) => theme.fontSizes[3]};
    font-weight: ${({ theme }) => theme.fontWeights[1]};
    line-height: ${({ theme }) => theme.lineHeights[3]};
    padding: 0 0 ${({ theme }) => theme.space[1]}px 0 !important;
    margin-right: ${({ theme }) => theme.space[6]}px !important;
    margin-bottom: ${({ theme }) => theme.space[3]}px !important;
    border-bottom: 2px solid transparent;
    &.active {
      color: ${({ theme }) => theme.colors.focus} !important;
      border-color: ${({ theme }) => theme.colors.focus} !important;
    }
  }
  & > div.grid__item {
    padding: 0 !important;
  }
`;

const StyledCommentsContainer = styled(CommentsContainer)`
  border-top-color: ${({ theme }) => theme.colors.horizon};
  padding-left: 0 !important;
  padding-right: 0 !important;
`;
